"use strict";

const gating = require("./walletGating");
const cartWallets = require("./cartWallets");
const walletRenderer = require("./walletRenderer");
const expressDom = require("./expressDom");
const diagnostics = require("./expressDiagnostics");
const preflight = require("./expressPreflight");
const confirmationForm = require("./expressConfirmationForm");
const buttonOptions = require("./walletButtonOptions");

/**
 * DGF-3054 — express wallets on the PDP.
 *
 * Replaces app_adyen_SFRA/.../adyen/express/product/expressPayments.js as the orchestrator while
 * reusing its ApplePay and GooglePay classes, and fixes three things that make the vendor version
 * unsafe on Superdry's PDP:
 *
 *   1. buildFallbackProduct (:24-33) defaults readyToOrder and available to TRUE when the DOM
 *      lookup misses. On Superdry it always misses — .global-availability is rendered only in the
 *      update-cart branch of addToCartActions.isml, never on a PDP — so a master product with no
 *      size chosen would be offered as express-buyable.
 *   2. It identifies the product with document.querySelector('[data-pid]') (:15-17, :98), the first
 *      such element anywhere in the document. Here the id comes from the server-rendered express
 *      container, and updates come from the product:updateAddToCart payload.
 *   3. renderInitialExpress (:39-61) hangs the first render on
 *      $('.quantity-select') option data-url. Superdry's PDP has no quantity selector at all, so
 *      that path never resolves and always drops into the defaulted fallback above.
 *
 * All dependencies are injected; js/product/expressPayments.js supplies the vendor classes through
 * the webpack `adyenSFRA` alias.
 */

const SURFACE = "pdp";

/** Guards event binding across bundle instances. */
const HANDLERS_BOUND_FLAG = "sdExpressProductHandlersBound";

let cachedResponse = null;
let resizeTimer = null;
/**
 * A variant selection that arrived before the payment methods response, replayed by init().
 * See the guard in renderForProduct for why this exists.
 */
let pendingProduct = null;

/**
 * Monotonically increasing render generation. PR #1979 finding 1.
 *
 * Every render pass claims one SYNCHRONOUSLY, before its first await, so the order recorded here is
 * the order the shopper made their selections in — not the order Adyen-CalculatePrice happened to
 * answer in. Past that point nothing writes the DOM, window.basketAmount or an SDK component unless
 * its generation is still the latest.
 *
 * What went wrong without it: every product:updateAddToCart started an independent pass, and each
 * pass awaits Adyen-CalculatePrice (resolveInitialAmount, :205) before it publishes the amount and
 * mounts. Two price requests are two unrelated POSTs through the vendor httpClient
 * (adyenSFRA/adyen/commons/httpClient.js) and nothing sequences them, so selecting size M and then
 * size L could leave M's continuation running LAST. It then published M's amount, remounted the
 * wallets, and rewrote #selected-express-product with M.
 *
 * That last write is what made this a charging defect rather than a cosmetic one. The vendor reads
 * that hidden input when the payment SHEET OPENS, not when the button is built:
 * applepay.js:262-265 (onClick) and googlepay.js:309-317 (onPaymentDataChanged, INITIALIZE) both
 * call createTemporaryBasket(), which does document.getElementById('express-product-form') and
 * JSON.parses #selected-express-product out of it (commons/index.js:51-55). So the shopper could
 * authorise a sheet for L and have M put in the temporary basket that is actually charged. The same
 * reversal could also remount an eligible render on top of a selection that had since become
 * ineligible, offering express on a size that is out of stock.
 *
 * Rejected alternatives:
 *   - Comparing the resolved id against the container's data-express-pid. That attribute is
 *     server-rendered once and never updated by product:updateAddToCart, so on a variation-group URL
 *     it describes the group and not the selection — the same reason readInitialProduct exists.
 *   - Aborting the in-flight price request. The vendor's httpClient owns it and exposes no abort,
 *     and Adyen-CalculatePrice is a read, so letting it land and discarding it is both simpler and
 *     honest about what actually happens on the wire.
 */
let renderGeneration = 0;

/**
 * Serialises the mount phase of a render — deliberately NOT the price fetch.
 *
 * The generation check alone cannot stop two passes being inside walletRenderer at once: a pass
 * tests its generation at the moment its price resolves, and a newer selection made after that test
 * passes its own test later while the first is still mounting. That matters because
 * walletRenderer.renderWallets empties the container before mounting (walletRenderer.js:71-73) and
 * removes its own element again when a component turns out to be unavailable (:115, :124) — so the
 * older pass would remove an element the newer pass has already taken out, which is a NotFoundError
 * in a real DOM. tests/.../fakeDom.js has a forgiving removeChild and would never show it. Making
 * walletRenderer re-entrant is not this fix (it is shared with the bag surfaces), so the phase is
 * made non-overlapping here instead.
 *
 * The price fetch stays outside the lock on purpose: it is the slow part, and holding the lock
 * across it would turn "M then L" into two serial round trips before L's button appeared.
 *
 * Accepted trade-off: a vendor getComponent() that never settles holds the lock, and later
 * selections then never mount. Not treated as worse than the alternative — a hung pass has already
 * left the container half-rendered and will still clear and re-mount it whenever it does resolve, so
 * letting a later pass in alongside it produces a container two passes are writing to rather than a
 * recovery. A timeout was considered and rejected: there is no non-arbitrary value for it, and
 * abandoning a half-mounted wallet is exactly the state this lock exists to prevent.
 *
 * Module-level rather than on window, unlike cartWallets' refresh state and HANDLERS_BOUND_FLAG:
 * the PDP loads this through the productDetail.js page bundle
 * (js/product/expressPayments.js) and the express markup is a remote include with no script tag of
 * its own, so there is exactly one instance per page.
 */
let mountChain = Promise.resolve();

/**
 * Claims the next render generation.
 *
 * Must be called before the caller's first await, or the ordering it records is the network's rather
 * than the shopper's.
 *
 * @returns {number} the claimed generation
 */
function claimRenderGeneration() {
    renderGeneration += 1;
    return renderGeneration;
}

/**
 * @param {number} generation - the generation claimed by the caller
 * @returns {boolean} whether a later selection has claimed a generation since
 */
function isSupersededRender(generation) {
    return generation !== renderGeneration;
}

/**
 * Runs `work` with exclusive use of the mount phase.
 *
 * @param {Function} work - async function performing the container writes
 * @returns {Promise<Object>} whatever work resolves to
 */
function withMountLock(work) {
    const run = mountChain.then(function runWork() {
        return work();
    });
    // The tail swallows both outcomes: a render that throws must not wedge the ones behind it, or a
    // single failed mount would leave express dead for the rest of the page.
    mountChain = run.then(function ignoreResult() {
        return null;
    }, function ignoreFailure() {
        return null;
    });
    return run;
}

/**
 * Records that a render pass declined to write because it had been superseded.
 *
 * Recorded rather than swallowed: a wallet that quietly fails to appear is the failure mode
 * expressDiagnostics exists for, and if this ever fires repeatedly on a real PDP it means selections
 * are outrunning Adyen-CalculatePrice and the last pass is the only one doing any work.
 *
 * @param {Object} deps - injected dependencies
 * @param {number} generation - the superseded generation
 * @param {string} phase - "clear", "mount" or "post-mount"
 * @returns {void}
 */
function recordSupersededRender(deps, generation, phase) {
    diagnostics.record(deps, {
        surface: SURFACE,
        wallet: "*",
        reason: "superseded-render",
        detail: `generation ${generation} of ${renderGeneration} discarded at ${phase}`
    });
}

/**
 * Reads the server-rendered eligibility for the first paint.
 *
 * These attributes come from the same product model that gates add-to-bag, rendered by
 * Product-RenderExpressPayments. Both are parsed strictly, so a missing attribute means "not
 * eligible" rather than "assume yes".
 *
 * @param {Object} container - the express container element
 * @returns {Object} product-shaped payload
 */
function readInitialProduct(container) {
    return {
        id: container && typeof container.getAttribute === "function"
            ? container.getAttribute("data-express-pid")
            : null,
        readyToOrder: gating.readBooleanAttribute(container, "data-express-ready-to-order"),
        available: gating.readBooleanAttribute(container, "data-express-available")
    };
}

/**
 * Resolves the amount for the payment sheet, in minor units.
 *
 * Delegates the arithmetic to the server (Adyen-CalculatePrice) and converts with the currency's
 * own fraction digits — never a literal 100. The vendor computes this as
 * Math.round(value * 100) (product/expressPayments.js:89), one of the four hard-coded
 * fraction-digit violations the shared amount contract exists to stop, and it also leaves Google
 * Pay's PDP amount at zero (:177) where Apple Pay gets a real one.
 *
 * @param {Object} deps - injected dependencies
 * @param {Object} product - product payload
 * @param {string} currency - currency from the payment methods response
 * @returns {Promise<Object>} { value, currency } with value in minor units
 */
async function resolveInitialAmount(deps, product, currency) {
    const amount = { value: 0, currency: currency };
    if (!product || !product.id || typeof deps.calculateProductPrice !== "function") {
        return amount;
    }
    try {
        const response = await deps.calculateProductPrice(product.id, 1);
        if (response && response.success && response.totalAmount) {
            const fractionDigits = deps.win && deps.win.fractionDigits;
            const minor = gating.toMinorUnits(response.totalAmount.value, fractionDigits);
            if (minor !== null) {
                amount.value = minor;
                amount.currency = response.totalAmount.currencyCode || currency;
            }
        }
    } catch (error) {
        // Leave the amount at zero rather than blocking the render: the wallet recalculates
        // through onShippingContactSelected / onPaymentDataChanged before authorisation, and the
        // server is the authority on what is charged.
        return amount;
    }
    return amount;
}

/**
 * Publishes the amount the PDP wallets will charge as window.basketAmount.
 *
 * The vendor wallet classes fall back to `JSON.parse(window.basketAmount)` whenever they are not
 * handed an initialAmount (applepay.js:25, googlepay.js:28, paypal.js:31), and on the PDP the
 * metadata's value describes the shopper's bag — which is usually empty and has nothing to do with
 * the product they are buying. Overwriting it with the resolved product amount keeps that fallback
 * truthful, and mirrors what cartWallets does after fetching the payment methods.
 *
 * @param {Object} deps - injected dependencies
 * @param {Object} amount - { value, currency } in minor units
 * @returns {void}
 */
function publishBasketAmount(deps, amount) {
    if (!deps.win || !amount || !amount.currency) {
        return;
    }
    deps.win.basketAmount = JSON.stringify({
        currency: amount.currency,
        value: amount.value
    });
}

/**
 * Builds the Adyen SDK component for one wallet on the PDP.
 *
 * @param {Object} deps - injected dependencies
 * @param {Object} paymentMethodsResponse - response from Adyen-GetExpressPaymentMethods
 * @param {Object} initialAmount - { value, currency } in minor units
 * @returns {Function} createComponent implementation for walletRenderer
 */
function buildComponentFactory(deps, paymentMethodsResponse, initialAmount) {
    return async function createComponent(walletType, walletConfiguration, element) {
        // Local wallets (DGF-4617 direct PayPal) own their whole flow — see cartWallets. Routed on
        // the container's data-express-local-wallets rather than the wallet name; see
        // walletGating.isLocalWalletOnContainer.
        if (gating.isLocalWalletOnContainer(element && element.parentNode, walletType)) {
            const localWallet = deps.localWallets && deps.localWallets[walletType];
            if (!localWallet) {
                return null;
            }
            return localWallet.createComponent({
                deps: deps,
                surface: SURFACE,
                container: element && element.parentNode
            });
        }

        // A lookup rather than the old `applepay ? ApplePay : GooglePay` ternary, whose fallthrough
        // constructed a Google Pay instance for ANY unrecognised type.
        const walletClasses = deps.walletClasses
            || { applepay: deps.ApplePay, googlepay: deps.GooglePay };
        const WalletClass = walletClasses[walletType];
        if (!WalletClass) {
            return null;
        }
        const wallet = new WalletClass(
            walletConfiguration,
            paymentMethodsResponse.applicationInfo,
            paymentMethodsResponse.adyenTranslations,
            true,
            initialAmount
        );
        // Both halves of the width fix: Google Pay needs buttonSizeMode 'fill' to stretch, and this
        // was missing on the PDP entirely, so its buttons sized to their own content.
        buttonOptions.applyButtonStyling(wallet, walletType);
        preflight.wrapWallet(wallet, deps, SURFACE);
        return wallet.getComponent();
    };
}

/**
 * Renders — or clears — the PDP wallets for a given product.
 *
 * @param {Object} deps - injected dependencies
 * @param {Object} product - product payload from product:updateAddToCart or the server attributes
 * @returns {Promise<Object>} render result
 */
async function renderForProduct(deps, product) {
    // Claimed before anything is awaited — see the renderGeneration comment. Claimed for EVERY
    // payload, including ineligible ones, because an ineligible selection has to be able to
    // supersede an in-flight eligible one: that is the "switched to an out-of-stock size while its
    // price was still loading" case, where the older pass would otherwise mount express on a
    // variant that cannot be bought.
    const generation = claimRenderGeneration();
    const container = expressDom.findExpressContainers(deps.doc, SURFACE)[0];
    const empty = { mounted: [], skipped: [] };

    if (!container || !cachedResponse) {
        // A variant selection can arrive BEFORE the payment methods response does, and dropping it
        // here used to lose the wallets for the rest of the page.
        //
        // Landing on a PDP with a size already chosen — from the wishlist, or any
        // getVariationProductLink URL (productHelpers.js:410-429) — produces exactly that ordering.
        // The link carries `product_<variationGroupID>_size`, which nothing server-side consumes, so
        // the server renders the VARIATION GROUP and data-express-ready-to-order is false. The client
        // then clicks the size button for real (product/detail.js:454-459), which fires
        // product:updateAddToCart — but init() binds that handler (:464) before it awaits
        // getExpressPaymentMethods() (:497), so the click lands inside the await window with
        // cachedResponse still null. The event was consumed and discarded, init() then rendered from
        // the server's variation-group attributes, and nothing ever fired again.
        //
        // So the payload is kept and replayed by init() once the response is in. Deliberately the
        // LAST one wins: if the shopper changed size several times while the request was in flight,
        // only their final choice is correct to mount.
        //
        // This replay and the render generation are separate mechanisms that compose rather than
        // compete, and this is the seam between them. Nothing has been awaited and nothing has been
        // written on this path, so the generation claimed above is simply spent — it exists only to
        // keep the counter monotonic with the shopper's clicks. init() replays the queued payload
        // through this same function (:509), which claims its own, later generation. So a replay can
        // supersede a render that is already in flight, and an in-flight render can never supersede
        // the replay of a selection made after it. pendingProduct answers "which payload", the
        // generation answers "may this pass still write" — neither needs to know about the other.
        if (!cachedResponse && product) {
            pendingProduct = product;
            diagnostics.record(deps, {
                surface: SURFACE,
                wallet: "*",
                reason: "selection-before-payment-methods",
                detail: "variant selected before the payment methods response arrived; queued for replay"
            });
        }
        return empty;
    }

    if (!gating.isProductExpressEligible(product)) {
        // Size and/or colour not chosen, or the variant is not orderable. Nothing is offered, and
        // anything previously mounted is removed rather than left showing a stale variant.
        diagnostics.record(deps, {
            surface: SURFACE,
            wallet: "*",
            reason: "product-not-eligible",
            detail: product ? `readyToOrder=${product.readyToOrder} available=${product.available}` : "no product"
        });
        return withMountLock(async function clearWallets() {
            if (isSupersededRender(generation)) {
                // A later selection is already waiting on this lock and will decide what the
                // container should show, so this pass does not touch it. Clearing anyway would be
                // harmless today but it is still a write from a superseded pass, and the whole point
                // of the generation is that there are none of those.
                recordSupersededRender(deps, generation, "clear");
                return empty;
            }
            await walletRenderer.renderWallets({
                doc: deps.doc,
                container: container,
                surface: SURFACE,
                walletTypes: [],
                adyenPaymentMethods: cachedResponse.AdyenPaymentMethods,
                createComponent: function noop() { return null; },
                nav: deps.nav
            });
            expressDom.setSurfaceVisibility(container, false);
            return empty;
        });
    }

    if (!expressDom.promoteTokenElement(deps.doc)) {
        return empty;
    }

    const currency = cachedResponse.amount ? cachedResponse.amount.currency : undefined;
    // The only long await on this path, and the one the reversal happens across. Everything after it
    // is a write — the published amount, the SDK components, the hidden product form — so all of it
    // is gated on this pass still being the latest.
    const initialAmount = await resolveInitialAmount(deps, product, currency);

    return withMountLock(async function mountWallets() {
        if (isSupersededRender(generation)) {
            // The shopper has selected something else since this price was asked for. Nothing is
            // published and nothing is mounted: window.basketAmount, the components' initialAmount
            // and #selected-express-product all describe THIS product, and publishing any of them
            // now is what let a shopper authorise for one variant and be charged for another.
            //
            // Safe to simply return, because the pass that superseded this one either already ran or
            // is waiting on this same lock — every claimed generation reaches this function, so the
            // newest one always gets to decide what the container ends up showing.
            recordSupersededRender(deps, generation, "mount");
            return empty;
        }

        publishBasketAmount(deps, initialAmount);

        // DGF-4617: keep the container's pid in step with the RESOLVED variant. The attribute is
        // server-rendered once with whatever pid the fragment was requested for — usually a master
        // or variation group.
        //
        // The original reason is obsolete: the direct-PayPal component no longer reads this at click
        // time to add the product to the real basket (it reads #selected-express-product and creates
        // a temporary basket), so a stale value can no longer post a master with no price. What the
        // attribute is still for is the supersede check — a variant switch mid-render compares
        // against it to decide whether an in-flight pass still owns the container — so it is still
        // written only by the latest generation, like every other write in this pass.
        if (product && product.id) {
            container.setAttribute("data-express-pid", product.id);
        }

        const result = await walletRenderer.renderWallets({
            doc: deps.doc,
            container: container,
            surface: SURFACE,
            // Same combination semantics as the bag surfaces: Adyen's offer list, widened by the
            // server-gated local wallets on the container, narrowed by any allow-list (DGF-4617).
            walletTypes: cartWallets.resolveWalletTypes(container, cachedResponse.pdpExpressMethods),
            adyenPaymentMethods: cachedResponse.AdyenPaymentMethods,
            createComponent: buildComponentFactory(deps, cachedResponse, initialAmount),
            nav: deps.nav
        });

        if (isSupersededRender(generation)) {
            // Superseded while the components were being built, so the buttons in the container are
            // for the previous selection. The hidden form is deliberately NOT written: the wallets
            // are hidden instead, and the pass waiting on this lock re-renders both together.
            //
            // Hiding rather than leaving them visible-but-stale is the conservative half of this.
            // Without the form, createTemporaryBasket throws on `new FormData(null)` before it can
            // POST anything (commons/index.js:52-53), so the worst case is a wallet that cannot
            // start rather than one that buys the wrong variant — but a button the shopper cannot
            // use should not be on screen either.
            expressDom.setSurfaceVisibility(container, false);
            recordSupersededRender(deps, generation, "post-mount");
            return empty;
        }

        if (result.mounted.length) {
            // Appended after the wallets, because renderWallets empties the container first.
            expressDom.upsertExpressProductForm(deps.doc, container, product);
            expressDom.matchWidthToAddToBag(deps.doc, container);
        }
        expressDom.setSurfaceVisibility(container, result.mounted.length > 0);
        diagnostics.recordRenderResult(deps, SURFACE, result, cachedResponse.pdpExpressMethods);

        return result;
    });
}

/**
 * Subscribes to variant selection.
 *
 * product:updateAddToCart is triggered on the add-to-cart buttons by
 * app_superdry/.../js/product/base.js:326 and bubbles to body, carrying
 * { product, $productContainer }. It is the only signal that reports the newly selected variant's
 * readyToOrder and available together, so it is the source of truth for the gate.
 *
 * Quick view fires the same event, so payloads from a quick view container are ignored: express is
 * PDP-only, and the quick view has no express markup to mount into anyway.
 *
 * @param {Object} deps - injected dependencies
 * @returns {void}
 */
function bindEvents(deps) {
    if (!deps.$ || !deps.win || deps.win[HANDLERS_BOUND_FLAG]) {
        return;
    }
    deps.win[HANDLERS_BOUND_FLAG] = true;

    // The measured add-to-bag width is a pixel value, so it goes stale on resize. Re-measuring is
    // cheap — it does not re-mount anything — and keeps the three-button column aligned.
    if (typeof deps.win.addEventListener === "function") {
        deps.win.addEventListener("resize", function onResize() {
            if (resizeTimer && typeof deps.win.clearTimeout === "function") {
                deps.win.clearTimeout(resizeTimer);
            }
            const remeasure = function remeasure() {
                const container = expressDom.findExpressContainers(deps.doc, SURFACE)[0];
                if (container) {
                    expressDom.matchWidthToAddToBag(deps.doc, container);
                }
            };
            resizeTimer = typeof deps.win.setTimeout === "function"
                ? deps.win.setTimeout(remeasure, 100)
                : remeasure();
        });
    }

    deps.$("body").on("product:updateAddToCart", function onUpdateAddToCart(event, response) {
        const $productContainer = response && response.$productContainer;
        if ($productContainer && $productContainer.closest && $productContainer.closest(".product-quickview").length) {
            return undefined;
        }
        // The promise is returned so the render is awaitable in tests; jQuery ignores it.
        return renderForProduct(deps, response && response.product);
    });
}

/**
 * Entry point.
 *
 * @param {Object} deps - injected dependencies
 * @returns {Promise<Object>} the first render result
 */
async function init(deps) {
    const container = expressDom.findExpressContainers(deps.doc, SURFACE)[0];
    if (!container) {
        // PDP express is off, or this is a quick view: the server rendered no markup at all.
        return { mounted: [], skipped: [] };
    }
    // DGF-4617: local wallets (direct PayPal) are gated by their own preference, so the PDP can
    // offer express even when Adyen express is off for it.
    const adyenPdpEnabled = deps.win && deps.win.areExpressPaymentsEnabledOnPdp === "true";
    const hasLocalWallets = Boolean(container.getAttribute(cartWallets.LOCAL_WALLETS_ATTRIBUTE));
    if (!adyenPdpEnabled && !hasLocalWallets) {
        return { mounted: [], skipped: [] };
    }

    // Must be in place before any wallet can authorise: the vendor builds the confirmation form
    // without a CSRF token and posts it natively to a route WP1 now CSRF-validates. Only relevant
    // (and only wired) when the Adyen wallets are on the page.
    if (deps.helpers) {
        confirmationForm.patchCreateShowConfirmationForm(deps.helpers, deps);
    }

    bindEvents(deps);

    try {
        // With Adyen express off there is nothing to ask Adyen for — an empty response keeps the
        // pipeline unchanged and the local wallets join via the container attribute.
        cachedResponse = (adyenPdpEnabled && typeof deps.getExpressPaymentMethods === "function")
            ? await deps.getExpressPaymentMethods()
            : { pdpExpressMethods: [], AdyenPaymentMethods: { paymentMethods: [] } };
    } catch (error) {
        expressDom.setSurfaceError(container, deps.errorMessage || "");
        return { mounted: [], skipped: [] };
    }
    if (!cachedResponse) {
        return { mounted: [], skipped: [] };
    }

    // A selection that raced the request above wins over the server-rendered attributes: it describes
    // the variant the shopper is actually looking at, whereas the container's attributes describe
    // whatever the server rendered — the variation group, on a preselected-size URL.
    return renderForProduct(deps, pendingProduct || readInitialProduct(container));
}

module.exports = {
    SURFACE: SURFACE,
    HANDLERS_BOUND_FLAG: HANDLERS_BOUND_FLAG,
    init: init,
    readInitialProduct: readInitialProduct,
    resolveInitialAmount: resolveInitialAmount,
    publishBasketAmount: publishBasketAmount,
    buildComponentFactory: buildComponentFactory,
    renderForProduct: renderForProduct,
    // exposed for tests
    _reset: function reset() {
        cachedResponse = null;
        resizeTimer = null;
        pendingProduct = null;
        renderGeneration = 0;
        mountChain = Promise.resolve();
    },
    _setResponse: function setResponse(response) {
        cachedResponse = response;
    }
};
